// src/features/tickets/components/TicketsPage.tsx
//
// Container-Page fuer die Ticket-Uebersicht.
// State und Daten-Logik sitzen seit Schritt 8 im useTickets-Hook; diese
// Komponente ist reine Praesentations- und Navigations-Schicht.
//
// Der Button "Meine" hat ein Auswahlmenue: dort laesst sich statt der eigenen
// Liste die Ansicht "Meine" einer Kollegin bzw. eines Kollegen waehlen. Der
// Button zeigt dann deren Namen. Die Standardansicht (Home-Symbol) bezieht
// sich nur auf die eigene Liste und ist waehrend dieser Auswahl gesperrt.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Home, ChevronDown, Check } from 'lucide-react';
import { useTickets } from '../hooks/useTickets';
import { TicketList } from './TicketList';
import { NewTicketModal } from './NewTicketModal';
import { TicketView } from '../types';
import { EmailSenderIdentity } from '../../../services/email/types';

const VIEW_LABELS: Record<TicketView, string> = {
  mine: 'Meine',
  pool: 'Pool',
  all: 'Alle'
};

const formatCount = (n: number): string => n.toLocaleString('de-DE');

export const TicketsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    tickets,
    loading,
    refreshing,
    loadingMore,
    totalCount,
    hasMore,
    loadError,
    searchActive,
    currentStaffId,
    staffList,
    view,
    setView,
    viewStaffId,
    setViewStaffId,
    showClosed,
    setShowClosed,
    searchTerm,
    setSearchTerm,
    standardView,
    savingStandardView,
    saveStandardView,
    reload,
    loadMore
  } = useTickets();

  // Modal-State bleibt lokal — gehoert zur UI, nicht zur Listen-Logik
  const [showNewModal, setShowNewModal] = useState(false);

  const handleTicketClick = (ticketId: string) => {
    navigate(`/tickets/${ticketId}`);
  };

  const handleNewTicketSuccess = () => {
    // Nach erfolgreicher Anlage Liste neu laden
    reload();
  };

  // Auswahl fuer die Ansicht "Meine": alle aktiven Mitarbeiter ausser einem
  // selbst, alphabetisch nach Name.
  const colleagues = useMemo(
    () => staffList
      .filter(s => s.id !== currentStaffId)
      .sort((a, b) => a.fullName.localeCompare(b.fullName, 'de')),
    [staffList, currentStaffId]
  );

  const selectedColleague = viewStaffId
    ? staffList.find(s => s.id === viewStaffId) ?? null
    : null;
  const viewingColleague = selectedColleague !== null;

  // Auswahl im Menue: eigene Liste (null) oder Kollegin/Kollege; wechselt
  // immer in die Ansicht "Meine".
  const handleSelectMineStaff = (staffId: string | null) => {
    setViewStaffId(staffId === currentStaffId ? null : staffId);
    setView('mine');
  };

  // Die Standardansicht gilt nur fuer die eigene Liste
  const standardLocked = view === 'mine' && viewingColleague;
  const isStandardView = standardView === view && !standardLocked;

  const homeButtonTitle = standardLocked
    ? 'Die Standardansicht gilt nur für Deine eigene Liste'
    : (isStandardView
      ? `„${VIEW_LABELS[view]}“ ist Deine Standardansicht`
      : `„${VIEW_LABELS[view]}“ als Standardansicht speichern`);

  // Ergebnis-Zaehler: Gesamtzahl, bei Teilanzeige zusaetzlich die angezeigte Menge
  const buildCounterText = (): string => {
    if (loading) return 'Lade Tickets...';
    const shown = tickets.length < totalCount
      ? `, ${formatCount(tickets.length)} angezeigt`
      : '';
    const base = searchActive
      ? `${formatCount(totalCount)} Treffer in allen Tickets inkl. geschlossener`
      : `${formatCount(totalCount)} Ticket${totalCount !== 1 ? 's' : ''}`;
    return `${base}${shown}${refreshing ? ' (wird aktualisiert)' : ''}`;
  };

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', paddingTop: '24px' }}>
      {/* Page-Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <button
          onClick={() => setShowNewModal(true)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 14px',
            backgroundColor: '#2563EB',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            boxShadow: '0 1px 2px 0 rgba(0,0,0,0.06)'
          }}
          onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#1D4ED8'; }}
          onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#2563EB'; }}
        >
          <Plus size={16} />
          Neues Ticket
        </button>
      </div>

      {/* View-Toggle (Meine mit Mitarbeiterauswahl / Pool / Alle) + Standardansicht speichern */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '16px'
      }}>
        <div style={{
          display: 'flex',
          gap: '4px',
          backgroundColor: '#F3F4F6',
          padding: '4px',
          borderRadius: '8px',
          width: 'fit-content'
        }}>
          <MineViewButton
            label={selectedColleague ? selectedColleague.fullName : 'Meine'}
            active={view === 'mine'}
            isStandard={standardView === 'mine' && !viewingColleague}
            colleagues={colleagues}
            selectedColleagueId={selectedColleague ? selectedColleague.id : null}
            onActivate={() => setView('mine')}
            onSelectStaff={handleSelectMineStaff}
          />
          <ViewToggleButton label="Pool" active={view === 'pool'} isStandard={standardView === 'pool'} onClick={() => setView('pool')} />
          <ViewToggleButton label="Alle" active={view === 'all'} isStandard={standardView === 'all'} onClick={() => setView('all')} />
        </div>

        <button
          onClick={() => { if (!isStandardView && !standardLocked) saveStandardView(); }}
          disabled={isStandardView || standardLocked || savingStandardView}
          title={homeButtonTitle}
          aria-label={homeButtonTitle}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '34px',
            height: '34px',
            padding: 0,
            backgroundColor: isStandardView ? '#EFF6FF' : 'white',
            color: isStandardView ? '#2563EB' : '#6B7280',
            border: `1px solid ${isStandardView ? '#BFDBFE' : '#D1D5DB'}`,
            borderRadius: '8px',
            cursor: isStandardView || standardLocked || savingStandardView ? 'default' : 'pointer',
            opacity: savingStandardView || standardLocked ? 0.6 : 1,
            transition: 'all 0.15s'
          }}
        >
          <Home size={16} />
        </button>
      </div>

      {/* Filter-Bar (Search + Geschlossene-Toggle) */}
      <div style={{
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <div style={{
          position: 'relative',
          flex: 1,
          maxWidth: '400px'
        }}>
          <Search
            size={16}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9CA3AF',
              pointerEvents: 'none'
            }}
          />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Suche in Betreff, Nachrichten, Ticketnummer, Kunde..."
            style={{
              width: '100%',
              padding: '8px 12px 8px 36px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '14px',
          color: '#374151',
          cursor: 'pointer',
          userSelect: 'none'
        }}>
          <input
            type="checkbox"
            checked={showClosed}
            onChange={(e) => setShowClosed(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          Geschlossene anzeigen
        </label>
      </div>

      {/* Ergebnis-Zaehler */}
      <div style={{
        fontSize: '13px',
        color: '#6B7280',
        marginBottom: '12px'
      }}>
        {buildCounterText()}
      </div>

      {/* Fehlerhinweis statt stiller leerer Liste */}
      {loadError && !loading && (
        <div style={{
          padding: '10px 14px',
          marginBottom: '12px',
          backgroundColor: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: '6px',
          color: '#B91C1C',
          fontSize: '14px'
        }}>
          Die Tickets konnten nicht geladen werden. Bitte die Seite neu laden; bleibt der Fehler bestehen, Tim informieren.
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: '#6B7280'
        }}>
          Lade Tickets...
        </div>
      ) : (
        <TicketList
          tickets={tickets}
          onTicketClick={handleTicketClick}
          currentStaffId={currentStaffId}
        />
      )}

      {/* Weitere laden */}
      {!loading && hasMore && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '16px'
        }}>
          <button
            onClick={() => loadMore()}
            disabled={loadingMore}
            style={{
              padding: '8px 16px',
              backgroundColor: 'white',
              color: '#374151',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: loadingMore ? 'default' : 'pointer',
              opacity: loadingMore ? 0.6 : 1
            }}
          >
            {loadingMore
              ? 'Lade weitere Tickets...'
              : `Weitere ${formatCount(Math.min(100, totalCount - tickets.length))} laden`}
          </button>
        </div>
      )}

      {/* Neues-Ticket-Modal */}
      <NewTicketModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        onSuccess={handleNewTicketSuccess}
      />
    </div>
  );
};

// ============================================
// View-Toggle Button (inline helper)
// ============================================

interface ViewToggleButtonProps {
  label: string;
  active: boolean;
  /** Markiert die gespeicherte Standardansicht mit einem kleinen Home-Symbol. */
  isStandard?: boolean;
  onClick: () => void;
}

const ViewToggleButton: React.FC<ViewToggleButtonProps> = ({ label, active, isStandard = false, onClick }) => {
  return (
    <button
      onClick={onClick}
      title={isStandard ? 'Standardansicht' : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 16px',
        backgroundColor: active ? 'white' : 'transparent',
        color: active ? '#111827' : '#6B7280',
        border: 'none',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: active ? '600' : '500',
        cursor: 'pointer',
        boxShadow: active ? '0 1px 2px 0 rgba(0, 0, 0, 0.08)' : 'none',
        transition: 'all 0.15s'
      }}
    >
      {isStandard && <Home size={12} />}
      {label}
    </button>
  );
};

// ============================================
// Button "Meine" mit Mitarbeiterauswahl (inline helper)
// ============================================

// Optisch wie ViewToggleButton. Der Beschriftungsteil aktiviert die Ansicht
// "Meine" mit der aktuellen Auswahl; der Pfeil oeffnet das Menue mit der
// eigenen Liste und den Kolleginnen und Kollegen. Das Menue schliesst bei
// Auswahl, Klick ausserhalb oder Escape.
interface MineViewButtonProps {
  label: string;
  active: boolean;
  /** Markiert die gespeicherte Standardansicht mit einem kleinen Home-Symbol. */
  isStandard: boolean;
  colleagues: EmailSenderIdentity[];
  /** Ausgewaehlte Kollegin bzw. ausgewaehlter Kollege; null = eigene Liste. */
  selectedColleagueId: string | null;
  onActivate: () => void;
  onSelectStaff: (staffId: string | null) => void;
}

const MineViewButton: React.FC<MineViewButtonProps> = ({
  label,
  active,
  isStandard,
  colleagues,
  selectedColleagueId,
  onActivate,
  onSelectStaff
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  const handleSelect = (staffId: string | null) => {
    setMenuOpen(false);
    onSelectStaff(staffId);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        backgroundColor: active ? 'white' : 'transparent',
        borderRadius: '6px',
        boxShadow: active ? '0 1px 2px 0 rgba(0, 0, 0, 0.08)' : 'none',
        transition: 'all 0.15s'
      }}>
        <button
          onClick={onActivate}
          title={isStandard ? 'Standardansicht' : undefined}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 6px 6px 16px',
            backgroundColor: 'transparent',
            color: active ? '#111827' : '#6B7280',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: active ? '600' : '500',
            cursor: 'pointer'
          }}
        >
          {isStandard && <Home size={12} />}
          {label}
        </button>
        <button
          onClick={() => setMenuOpen(o => !o)}
          title="Liste einer anderen Person anzeigen"
          aria-label="Liste einer anderen Person anzeigen"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '6px 10px 6px 4px',
            backgroundColor: 'transparent',
            color: active ? '#111827' : '#6B7280',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          <ChevronDown size={14} />
        </button>
      </div>

      {menuOpen && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            zIndex: 20,
            minWidth: '220px',
            backgroundColor: 'white',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
            padding: '4px'
          }}
        >
          <MineMenuItem
            label="Meine"
            selected={selectedColleagueId === null}
            onClick={() => handleSelect(null)}
          />
          {colleagues.length > 0 && (
            <div style={{ height: '1px', backgroundColor: '#F3F4F6', margin: '4px 0' }} />
          )}
          {colleagues.map(c => (
            <MineMenuItem
              key={c.id}
              label={c.fullName}
              selected={selectedColleagueId === c.id}
              onClick={() => handleSelect(c.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface MineMenuItemProps {
  label: string;
  selected: boolean;
  onClick: () => void;
}

const MineMenuItem: React.FC<MineMenuItemProps> = ({ label, selected, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      role="menuitemradio"
      aria-checked={selected}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        width: '100%',
        padding: '8px 12px',
        backgroundColor: hovered ? '#F3F4F6' : 'transparent',
        color: '#111827',
        border: 'none',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: selected ? '600' : '400',
        textAlign: 'left',
        cursor: 'pointer',
        whiteSpace: 'nowrap'
      }}
    >
      {label}
      {selected && <Check size={14} color="#2563EB" />}
    </button>
  );
};
